import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { ROUTE_PATHS } from "@/lib/index";
import Dashboard from "@/pages/Dashboard";
import Analytics from "@/pages/Analytics";
import Patients from "@/pages/Patients";
import Reports from "@/pages/Reports";

/**
 * Healthcare Analytics Dashboard - Root Application
 * 
 * This application provides a comprehensive suite for medical data analysis,
 * patient management, and hospital performance reporting using a modern
 * 3D-enhanced medical theme.
 * 
 * © 2026 Healthcare Data Systems
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        {/* Standard UI Toasts for high-priority alerts */}
        <Toaster />
        {/* Sonner for subtle system notifications */}
        <Sonner position="top-right" expand={false} richColors />
        
        <HashRouter>
          <Routes>
            {/* Primary Dashboard Overview */}
            <Route 
              path={ROUTE_PATHS.DASHBOARD} 
              element={<Dashboard />} 
            />

            {/* Deep Analytics & 3D Visualization */}
            <Route 
              path={ROUTE_PATHS.ANALYTICS} 
              element={<Analytics />} 
            />

            {/* Patient Management & Records */}
            <Route 
              path={ROUTE_PATHS.PATIENTS} 
              element={<Patients />} 
            />

            {/* Performance & Financial Reporting */}
            <Route 
              path={ROUTE_PATHS.REPORTS} 
              element={<Reports />} 
            />

            {/* Catch-all redirect to Dashboard */}
            <Route 
              path="*" 
              element={<Navigate to={ROUTE_PATHS.DASHBOARD} replace />} 
            />
          </Routes>
        </HashRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
